import { PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { claimGlobalModal, globalModalOwner, releaseGlobalModal } from 'utils/globalModalScope';

import GlobalContext from './GlobalContext';
import { useIsActiveTable } from './TableActiveContext';
import { TableIdContext } from './TableIdContext';

/**
 * Gives each table panel its own copy of the GlobalContext state that is really
 * per-table: the buy-in family and the seat the player picked.
 *
 * GlobalProvider is mounted at the app root (index.tsx), so `openAskBuyin` is
 * ONE boolean for every open table. In the single-table carousel that broke
 * multi-tabling in two ways:
 *
 * 1. The buy-in Table 1 opened showed up on Table 2 after a swipe (it is the
 *    same flag, so Table 2's own panel rendered it as its own).
 * 2. Worse, it BLOCKED Table 2: with the flag already `true`, Table 2's "Join
 *    Table" could not raise it, so no buy-in modal ever appeared for that table
 *    and the player could not sit down.
 *
 * The multi-table grid never had either problem — each cell gets its own
 * GlobalProvider (see MultiTableGrid) — so this gives carousel panels the same
 * independence, for just the fields that belong to a table.
 *
 * Writes from OUTSIDE any panel still work: App.tsx raises the buy-in when the
 * engine verifies a private-table password, and clears the password prompt.
 * Those writes claim no table (see utils/globalModalScope), so the table the
 * player is on adopts them — exactly as before.
 *
 * Everything else in GlobalContext (session, duplicate access, location,
 * cashier, error modal, sound) passes straight through: it is genuinely
 * app-wide.
 */

/** Registry keys — one per scoped field. */
const FIELD = {
  openAskBuyin: 'openAskBuyin',
  isTriggerBuyIn: 'isTriggerBuyIn',
  closeBuyInModal: 'closeBuyInModal',
  openWaitForBuyIn: 'openWaitForBuyIn',
  openAskPassword: 'openAskPassword',
  manuallyAssignedSeatId: 'manuallyAssignedSeatId'
} as const;

/**
 * One table's copy of an app-level value.
 *
 * `emptyValue` is the closed/unset value: writing it releases this table's claim
 * so another table can adopt the next app-level write.
 */
const useScopedValue = <T,>(
  field: string,
  appValue: T,
  setAppValue: (value: T) => void,
  emptyValue: T,
  gameID: string,
  isActiveTable: boolean
): [T, (value: T) => void] => {
  // Only the table the player is on may adopt an app-level value, and only when
  // no OTHER table raised it.
  const mayAdopt = () => {
    const owner = globalModalOwner(field);
    return isActiveTable && (owner === null || owner === gameID);
  };

  const [value, setValue] = useState<T>(() => (mayAdopt() ? appValue : emptyValue));
  const [seenAppValue, setSeenAppValue] = useState<T>(appValue);

  if (appValue !== seenAppValue) {
    // Adjusting state during render (React's documented pattern for deriving
    // from a changed input) rather than in an effect: an effect would settle
    // this after paint, showing one frame of the pre-adoption value.
    setSeenAppValue(appValue);
    if (mayAdopt()) setValue(appValue);
  }

  const set = useCallback(
    (next: T) => {
      setValue(next);
      if (next === emptyValue) releaseGlobalModal(field);
      else claimGlobalModal(field, gameID);
      // Mirror to the app-level value so code outside the panels keeps seeing
      // what the player has open.
      setAppValue(next);
    },
    [emptyValue, field, gameID, setAppValue]
  );

  // A closed table must not keep a claim, or no other table could adopt an
  // app-level write again.
  useEffect(
    () => () => {
      if (globalModalOwner(field) === gameID) releaseGlobalModal(field);
    },
    [field, gameID]
  );

  return [value, set];
};

export const TableGlobalScope = ({ children }: PropsWithChildren<unknown>) => {
  const app = useContext(GlobalContext);
  const gameID = useContext(TableIdContext);
  const isActiveTable = useIsActiveTable();

  const [openAskBuyin, setOpenAskBuyin] = useScopedValue(
    FIELD.openAskBuyin,
    app.openAskBuyin,
    app.setOpenAskBuyin,
    false,
    gameID,
    isActiveTable
  );
  const [isTriggerBuyIn, setIsTriggerBuyIn] = useScopedValue(
    FIELD.isTriggerBuyIn,
    app.isTriggerBuyIn,
    app.setIsTriggerBuyIn,
    false,
    gameID,
    isActiveTable
  );
  const [closeBuyInModal, setCloseBuyInModal] = useScopedValue(
    FIELD.closeBuyInModal,
    app.closeBuyInModal,
    app.setCloseBuyInModal,
    false,
    gameID,
    isActiveTable
  );
  const [openWaitForBuyIn, setOpenWaitForBuyIn] = useScopedValue(
    FIELD.openWaitForBuyIn,
    app.openWaitForBuyIn,
    app.setOpenWaitForBuyIn,
    false,
    gameID,
    isActiveTable
  );
  const [openAskPassword, setOpenAskPassword] = useScopedValue(
    FIELD.openAskPassword,
    app.openAskPassword,
    app.setOpenAskPassword,
    false,
    gameID,
    isActiveTable
  );
  // The seat the player tapped on THIS table. App-wide, it made a buy-in on the
  // next table sit down at a seat number picked on the previous one.
  const [manuallyAssignedSeatId, setManuallyAssignedSeatId] = useScopedValue(
    FIELD.manuallyAssignedSeatId,
    app.manuallyAssignedSeatId,
    app.setManuallyAssignedSeatId,
    0,
    gameID,
    isActiveTable
  );

  const value = useMemo(
    () => ({
      ...app,
      openAskBuyin,
      setOpenAskBuyin,
      isTriggerBuyIn,
      setIsTriggerBuyIn,
      closeBuyInModal,
      setCloseBuyInModal,
      openWaitForBuyIn,
      setOpenWaitForBuyIn,
      openAskPassword,
      setOpenAskPassword,
      manuallyAssignedSeatId,
      setManuallyAssignedSeatId
    }),
    [
      app,
      openAskBuyin,
      setOpenAskBuyin,
      isTriggerBuyIn,
      setIsTriggerBuyIn,
      closeBuyInModal,
      setCloseBuyInModal,
      openWaitForBuyIn,
      setOpenWaitForBuyIn,
      openAskPassword,
      setOpenAskPassword,
      manuallyAssignedSeatId,
      setManuallyAssignedSeatId
    ]
  );

  return <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>;
};

export default TableGlobalScope;
